import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { RouterLink } from '@angular/router';
import { bookedJourneyLeg, ticketVerifyUrl } from '@buspro/shared/common';
import { TenantUsersDto, TicketDetailsDto, TicketsApi } from '@buspro/web/apis';
import { DEFAULT_COMPANY_LOGO, WEB_CONFIG } from '@buspro/web/core';
import { WebAppEnvironment } from '@buspro/web/core';
import { SeatNamePipe } from '@buspro/web/shared';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { QRCodeModule } from 'angularx-qrcode';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzQRCodeModule } from 'ng-zorro-antd/qr-code';
import { MomentModule } from 'ngx-moment';
import { Observable, lastValueFrom } from 'rxjs';
import { PrintService } from './print.service';

@Component({
  selector: 'tenant-parcel-sticker-print-sheet',
  standalone: true,
  imports: [SeatNamePipe, CommonModule, MomentModule, RouterLink, NzQRCodeModule, NzIconModule, QRCodeModule],
  template: `
    <div class="invoice bg-gray-100">
      <div class="print-button-container">
        <button
          class="btn btn-primary btn-lg shadow-lg"
          (click)="printService.onDataReady()"
        >
          <span class="fe fe-printer"></span> Print Ticket
        </button>
      </div>

      @if (ticketQuery.isFetched()){
      <div
        id="ticket"
        class="card bg-white w-full p-4 mx-auto space-y-8"
        [ngStyle]="{ width: '80mm' }"
      >
        <div class="flex justify-between items-center">
          <div>
            <div class="font-bold text-primary-focus text-lg">
              {{ user!.company.name }}
            </div>
            <div class="text-gray-700 text-">
              Customer Receipt
              <!--                Your reservation is now confirmed-->
            </div>
          </div>
          <div class="w-20">
            <img [src]="user!.company.logoUrl" alt="company logo" />
          </div>
        </div>

        <!-- Verification Badge -->
        <div *ngIf="ticket.seatPassenger.isVerified" class="flex justify-center items-center">
          <div class="badge badge-success flex items-center gap-1">
            <span class="text-white">Verified</span>
            <span class="fe fe-check-circle text-white"></span>
          </div>
        </div>

        <div class="divide-y space-y-4">
          <div class="space-y-2">
            <div class="flex justify-between">
              <div class="text-xs text-gray-500">Travel Route</div>
              <div class="text-right text-xs font-semibold">
                {{ ticket.operation.routeName }}
              </div>
            </div>
            <!-- The leg the fare is for. The route above is the bus's whole
                 run; the total below buys only this part of it. -->
            <div *ngIf="journeyLeg(ticket)" class="flex justify-between">
              <div class="text-xs text-gray-500">Your Journey</div>
              <div class="text-right text-xs font-semibold">
                {{ journeyLeg(ticket) }}
              </div>
            </div>
            <div class="flex justify-between">
              <div class="text-xs text-gray-500">Departure Date</div>
              <div class="text-right text-xs font-semibold">
                {{ ticket.operation.departureTime | amDateFormat : 'll' }}
              </div>
            </div>
            <div class="flex justify-between">
              <div class="text-xs text-gray-500">Departure Time</div>
              <div class="text-right text-xs font-semibold">
                {{ ticket.operation.departureTime | amDateFormat : 'LT' }}
              </div>
            </div>
            <div class="flex justify-between">
              <div class="text-xs text-gray-500">Bus No.</div>
              <div class="text-right text-xs font-semibold">
                {{ ticket.operation.busName }}
              </div>
            </div>
            <div class="flex justify-between">
              <div class="text-xs text-gray-500">Seat Class</div>
              <div class="text-right text-xs font-semibold">
                {{ ticket.bookingSeat.class }}
              </div>
            </div>
            <div class="flex justify-between">
              <div class="text-xs text-gray-500">Seat Number</div>
              <div class="text-right text-xs font-semibold">
                {{ ticket.bookingSeat.nickName | seatName: ticket.bookingSeat.seatNumber }}
              </div>
            </div>
          </div>
          <div class="space-y-2 pt-4">
            <div class="flex justify-between">
              <div class="text-xs text-gray-500">Customer Name</div>
              <div class="text-right text-xs font-semibold">
                {{ ticket.seatPassenger.customerName }}
              </div>
            </div>
            <div class="flex justify-between">
              <div class="text-xs text-gray-500">Customer Phone Number</div>
              <div class="text-right text-xs font-semibold">
                {{ ticket.seatPassenger.customerPhoneNumber }}
              </div>
            </div>
            <div class="flex justify-between">
              <div class="text-xs text-gray-500">Served By</div>
              <div class="text-right text-xs font-semibold">
                {{ ticket.seatPassenger.servedBy }}
              </div>
            </div>
          </div>
          <div class="pt-2">
            <div class="flex justify-between">
              <div class="font-bold">Total</div>
              <!-- <div>
                  <nz-tag *ngIf="selected.isOccupied" nzColor="green">Booked & Paid</nz-tag>
                  <nz-tag *ngIf="!selected.isOccupied" nzColor="geekblue">Booked</nz-tag>
                </div> -->
              <div class="font-bold text-success">
                {{ ticket.seatPassenger.fare | currency : 'UGX ' }}
              </div>
            </div>
            <div
              *ngIf="ticket.bookingSeat.activePassenger"
              class="text-xs mt-4"
            >
              Note: Reservation will be rendered void if not paid within an hour
              to departure time.
            </div>
          </div>
        </div>

        <!-- Add QR Code section before the footer -->
        <div class="flex flex-col items-center mt-4">
          <h4 class="text-sm font-semibold mb-2">Scan to verify ticket</h4>

          <qrcode [qrdata]="verificationUrl"  [width]="150" [errorCorrectionLevel]="'M'"></qrcode>

          <div class="text-xs text-gray-500 mt-2">Ticket #: {{ ticket.seatPassenger.ticketNumber }}</div>
          <div *ngIf="ticket.seatPassenger.isVerified" class="text-xs text-gray-500">
            Verified by: {{ ticket.seatPassenger.verifiedBy }} on {{ ticket.seatPassenger.verifiedAt | amDateFormat: 'lll' }}
          </div>
        </div>

        <!-- Terms and Conditions Section -->
        <div class="border-t pt-3 mt-3">
          <h4 class="text-xs font-semibold text-center mb-2">Terms & Conditions</h4>
          <ul class="text-xxs text-gray-600 space-y-1">
            <li>1. This ticket is valid only for the journey, date, and time specified.</li>
            <li>2. Passengers must arrive at least 30 minutes before departure time.</li>
            <li>3. Tickets are non-refundable but can be rescheduled up to 24 hours before departure.</li>
            <li>4. Each passenger is allowed one piece of luggage not exceeding 20kg.</li>
            <li>5. The company is not responsible for any loss or damage to passenger belongings.</li>
          </ul>
        </div>
      </div>
      } @else{
      <!--      <div>Something went wrong</div>-->
      }
    </div>
  `,
  styles: `

    .invoice {
      page-break-after: always;
    }

      .invoice:last-child {
        page-break-after: avoid;
      }

      .print-button-container {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 1000;
      }

      .btn-lg {
        padding: 0.75rem 1.5rem;
        font-size: 1.25rem;
      }

      .text-xxs {
        font-size: 0.65rem;
        line-height: 1rem;
      }

@media print {
  .print-button-container {
    display: none;
  }

  #ticket{
    width: 88mm;
  }

  @page {
    size: 80mm auto;
    margin: 0;
  }
}
  `,
})
export class PassengerTicketPrintSheetComponent {
  private ngxAuthService = inject(NgxAuthService);
  printService = inject(PrintService);
  private ticketsApi = inject(TicketsApi);
  protected readonly environment = inject<WebAppEnvironment>(WEB_CONFIG);

  compositeKeys = signal<string | null>(null);

  authUser$: Observable<TenantUsersDto | null> = this.ngxAuthService.state;
  userSignal = toSignal(this.authUser$, { initialValue: null });

  ticketQuery = injectQuery(() => {
    return {
      queryKey: ['passenger-ticket-to-print', this.compositeKeys()],
      queryFn: () => {
        const ids = this.compositeKeys()!.split('|');
        const operationId = ids[0];
        const bookingSeatId = ids[1];
        const passengerId = ids[2];
        console.log(
          'destructured keys',
          'operationId',
          operationId,
          'bookingSeatId',
          bookingSeatId,
          'passengerId',
          passengerId,
        );
        return lastValueFrom(
          this.ticketsApi.ticketsControllerGetTicketDetails({
            passengerId,
            bookingSeatId,
            operationId,
          }),
        );
      },
      select: (data: TicketDetailsDto) => {
        if (data) {
          this.printService.onDataReady();
        }
        return data;
      },
      enabled: !!this.compositeKeys(),
    };
  });

  get user() {
    return this.userSignal()!;
  }

  get ticket() {
    return this.ticketQuery.data()!;
  }

  /**
   * The leg this passenger booked, "From → To", read off the route's own
   * sub-route rather than the name snapshotted onto the passenger at sale
   * time — a place rename never rewrites that snapshot, so a reprint would
   * otherwise carry a terminal name nobody uses any more.
   *
   * Takes the ticket as an argument because the template reads it inside the
   * `@if (ticketQuery.isFetched())` block, where `ticket` is already narrowed.
   */
  journeyLeg(ticket: TicketDetailsDto): string {
    return bookedJourneyLeg(ticket.seatPassenger, ticket.operation?.route?.subRoutes);
  }

  get verificationUrl(): string {
    if (!this.ticket || !this.ticket.seatPassenger) return '';

    // Use the public website URL from environment
    const baseUrl = this.environment.publicWebsiteUrl || 'https://buspro.biz';
    // The QR names the company: a short ticket number is unique only within one,
    // so a scan has to say which operator issued it. The public page still
    // accepts the legacy `?ticketNumber=` link for every slip already printed.
    return ticketVerifyUrl(baseUrl, this.ticket.seatPassenger.companyId, this.ticket.seatPassenger.ticketNumber);
  }

  constructor(route: ActivatedRoute) {
    console.log('composite keys', route.snapshot.params['compositeKeys']);
    this.compositeKeys.set(route.snapshot.params['compositeKeys']);
  }

  protected readonly companyLogoUrl = DEFAULT_COMPANY_LOGO;
}
